import React from "react";
import type {GetServerSidePropsContext} from "next";
import type {PageProps} from "../routing/types";
import HomepageLayout from "../components/page_layouts/HomepageLayout";
import "../utility/i18n";
import ErrorPage from "../components/page_layouts/ErrorPage";
import HtmlHeadContent from "../components/layouts/head/HtmlHeadContent";
import BlankPageLayout from "../components/page_layouts/BlankPageLayout";
import Analytics from "../extenions/googleTagManager/Analytics";
import CCOComment from "../components/fundamentals/CCOComment";
import CCOChatbot from "../components/fundamentals/CCOChatbot";
import {PageProvider} from "../context/PageContext";
import {extractTaxonomies, getPageLayoutType, isPageWithHiddenNavi} from "../components/page_layouts/pageLayoutsUtils";
import {MemoryCache} from 'memory-cache-node';
import {checkForFreshnessOncePerSlug,} from "../extenions/queryCache/CacheLayer";
import {PreviewProvider} from "../context/PreviewContext";
import VWO from "../extenions/vwo/VWO";
import {parseSlugSegments} from "../routing/slugParser";
import {PAGE_ERROR, PAGE_FOUND, PAGE_NOT_FOUND, PAGE_REDIRECT} from "./config/settings";
import {resolveRoute} from "../routing/routeResolver";
import {useInitializeSession} from "../hooks/useInitializeSession";
import {checkPreview} from "../utility/previewUtil";
import EasyLangLayout from "../components/page_layouts/EasyLangLayout";
import {isMobileUserAgentHeader} from "../utility/userAgent";

// Diese Variable wird von allen Threads genutzt!
const globalMemory = global as unknown as { memoryCache: MemoryCache<string, unknown> };

// Singleton hier!!!
export const getMemoryCache = async () => {
    if (globalMemory.memoryCache) {
        return globalMemory.memoryCache;
    }
    const itemsExpirationCheckIntervalInSecs = 60;
    const maxItemCount = 1000000;
    const memoryCache = new MemoryCache<string, unknown>(itemsExpirationCheckIntervalInSecs,
        maxItemCount);
    globalMemory.memoryCache = memoryCache;
    return memoryCache;
}

function PageContainer(props: PageProps) {
    useInitializeSession(props.segment);

    if (!props.isEmpty && props.page) {
        const layout = getPageLayoutType(props.page);
        const isHideNavi = props.page && isPageWithHiddenNavi(props.page);
        const taxonomies = extractTaxonomies(props);

        const renderLayout = () => {
            const commonProps = {
                page: props.page,
                navigation: props.navigation,
                CAEGlobalSettings: props.CAEGlobalSettings,
                currentURL: props.currentURL,
                content: props.content,
                csrfToken: props.csrfToken,
                credentials: props.credentials,
                donationInitializerData: props.donationInitializerData ?? undefined
            };

            switch (layout) {
                case 'EasyLangLayout':
                    return <EasyLangLayout {...commonProps} isHideNavi={isHideNavi ?? undefined}
                                           pageSettings={props.pageSettings}/>;
                case 'BlankPageLayout':
                    return <BlankPageLayout {...commonProps} />;
                default:
                    return <HomepageLayout {...commonProps} isHideNavi={isHideNavi ?? undefined}
                                           pageSettings={props.pageSettings}/>;
            }
        };

        return <PageProvider page={props.page} siteId={props.siteId} rootUrl={props.rootUrl}
                             taxonomies={taxonomies} isMobileUserAgent={!!props.isMobileUserAgent}>
            <PreviewProvider isPreview={!!props.preview}>
                <HtmlHeadContent CAEGlobalSettings={props.CAEGlobalSettings} page={props.page}
                                 content={props.content}
                                 currentURL={props.currentURL} isPreview={props.preview}/>
                <CCOComment text={props.comment}/>
                <Analytics gtmId={props.gtmId} referrer={props.referrer}/>
                <VWO vwoId={props.vwoId}/>
                {renderLayout()}
                <CCOChatbot/>
            </PreviewProvider>
        </PageProvider>
    } else {
        return <PageProvider page={props.rootPage} siteId={props.siteId} rootUrl={props.rootUrl}
                             taxonomies={[]} isMobileUserAgent={!!props.isMobileUserAgent}>
            <ErrorPage statusCode={404} rootUrl={props.rootUrl} navigation={props.navigation}
                       page={props.rootPage} CAEGlobalSettings={props.CAEGlobalSettings}/>
        </PageProvider>
    }
}

async function checkForServerDataFreshness(slugKey: string): Promise<{
    memoryCache: MemoryCache<string, unknown>
}> {
    const memoryCache = await getMemoryCache();
    const ready = await checkForFreshnessOncePerSlug(memoryCache, slugKey);
    if (!ready) {
        console.warn("Something went wrong while loading the eTag from the server.");
    }
    return {memoryCache};
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const {req, res, query} = context;
    const isPreview = checkPreview(req);
    const slug = context.query.slug ? context.query.slug : [];
    const slugKey = Array.isArray(slug) ? slug.join("/") : String(slug);
    const {memoryCache} = await checkForServerDataFreshness(slugKey);

    const slugArray = Array.isArray(slug) ? slug : (slug ? [slug] : []);
    const parsedSlug = parseSlugSegments(slugArray, req.headers['host']);

    const result = await resolveRoute(parsedSlug, {
        req, res, query, memoryCache, isPreview
    });
    const isMobileUserAgent = isMobileUserAgentHeader(req.headers['user-agent']);

    switch (result.type) {
        case PAGE_REDIRECT:
            return {
                redirect: {
                    destination: result.destination,
                    statusCode: typeof result.statusCode === 'number' ? result.statusCode : 302
                }
            };
        case PAGE_NOT_FOUND:
            res.statusCode = 404;
            return {props: {...result.props, isMobileUserAgent}};
        case PAGE_ERROR:
            console.error("Routing error:", result.error);
            return {props: {...(result.props || {isEmpty: true}), isMobileUserAgent}};
        case PAGE_FOUND:
        default:
            return {props: {...result.props, isMobileUserAgent}};
    }
}

export default PageContainer